import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {LOQATE_UNKNOWN_KEY_ERROR, LOQATE_GENERIC_ERROR} from '../constants'
export class LoqateAPI {
    async loqateFetch(apiUrl, method, body, removeLimit) {
        const headers = {
            'Content-Type': 'application/json'
        }

        if (body) {
            body = await this.buildBody(body)
            if (removeLimit) {
                delete body.addressLimit
            }
        }

        let response
        try {
            response = await fetch(apiUrl, {
                method: method,
                headers: headers,
                ...(body && {
                    body: JSON.stringify(body)
                })
            })
        } catch {
            console.warn('Loqate request failed')
        }

        if (!response?.ok) {
            return {}
        }

        const responseJson = await response.json()

        return responseJson
    }

    async apiDetails() {
        const {
            app: {commerceAPI}
        } = getConfig()
        let locale = 'en-US',
            country = 'US',
            loqateApiKey,
            enableLoqate = true,
            loqateAddressCleans,
            loqateAddressCapture,
            loqateAddressRetrieve,
            addressLimit
        const {configs} = commerceAPI.checkoutConfig || {}
        try {
            if (configs) {
                const parsedConfigs = JSON.parse(configs)
                loqateApiKey = parsedConfigs.loqateApiKey
                enableLoqate = parsedConfigs.enableLoqate
                loqateAddressCleans = parsedConfigs.loqateAddressCleans
                loqateAddressCapture = parsedConfigs.loqateAddressCapture
                loqateAddressRetrieve = parsedConfigs.loqateAddressRetrieve
                addressLimit = parsedConfigs.addressLimit
            }
        } catch (e) {
            console.error('Error in parsing checkoutConfig', e.message)
        }
        return {
            locale: locale,
            enableLoqate: enableLoqate,
            country: country,
            loqateApiKey: loqateApiKey,
            loqateAddressCleans: loqateAddressCleans,
            loqateAddressCapture: loqateAddressCapture,
            loqateAddressRetrieve: loqateAddressRetrieve,
            addressLimit: addressLimit
        }
    }

    async buildBody(address) {
        const apiDetails = await this.apiDetails()
        const loqateApiKey = apiDetails.loqateApiKey
        const requestBody = {
            Key: loqateApiKey,
            Addresses: [],
            Options: {
                Enhance: true,
                Process: 'Verify'
            }
        }
        requestBody.Addresses.push(address)
        return requestBody
    }

    getMatchScore(loqateAddress) {
        var matchScoreString =
            loqateAddress && loqateAddress.AVC ? loqateAddress.AVC.split('-')[3] : ''
        var matchScoreInt = matchScoreString.length ? parseInt(matchScoreString, 10) : 0

        return matchScoreInt
    }

    shouldShowError(loqateAddress, matchscore) {
        if (loqateAddress && matchscore) {
            var AVCVerificationLevel =
                loqateAddress && loqateAddress.AVC ? loqateAddress.AVC.substr(0, 2) : ''

            if (
                (AVCVerificationLevel === 'V4' || AVCVerificationLevel === 'V5') &&
                matchscore > 94
            ) {
                return {
                    showAddressVerificationModal: false,
                    showError: false
                }
            }

            if (
                (AVCVerificationLevel === 'P4' ||
                    AVCVerificationLevel === 'V4' ||
                    AVCVerificationLevel === 'V5') &&
                matchscore > 79
            ) {
                return {
                    showAddressVerificationModal: true,
                    showError: false
                }
            }
        }
        return {
            showAddressVerificationModal: false,
            showError: true
        }
    }

    showAddressVerificationModal
}

function convertToLowercase(value) {
    return value ? value?.toLowerCase()?.replace(/\b\w/g, (char) => char.toUpperCase()) : value
}

// Loqate returns US addresses in USPS format: the secondary unit (Apt/Ste/Unit)
// is appended to Line1 and Line2 comes back empty. SubBuilding carries it
// separately, so promote it into address2 and strip it off Line1.
export function splitLoqateAddressLines(item = {}) {
    let address1 = (item.Line1 || '').trim()
    const address2 = (item.Line2 || '').trim()

    if (address2) return {address1, address2}

    const subBuilding = (item.SubBuilding || '').trim()
    if (!subBuilding) return {address1, address2: ''}

    if (address1.toLowerCase().endsWith(subBuilding.toLowerCase())) {
        address1 = address1
            .slice(0, address1.length - subBuilding.length)
            .replace(/[\s,]+$/, '')
            .trim()
    }

    return {address1, address2: subBuilding}
}

const useLoqate = () => {
    const loqate = new LoqateAPI()
    return {
        async getAddressValidation(address) {
            const apiDetails = await loqate.apiDetails()
            try {
                const method = 'POST'
                const addressObj = {
                    Address1: convertToLowercase(address.address1),
                    Address2: convertToLowercase(address.address2),
                    AdministrativeArea: address.stateCode,
                    Country: apiDetails.country,
                    Locality: convertToLowercase(address.city),
                    PostalCode: address.postalCode
                }
                const endpoint = apiDetails.loqateAddressCleans
                const response = await loqate.loqateFetch(endpoint, method, addressObj, true)
                const matchedAddress = response && response.length ? response[0].Matches : []
                const enteredAddress = response && response.length && response[0].Input
                const matchscore = loqate.getMatchScore(matchedAddress?.[0])
                const {showError, showAddressVerificationModal} = loqate.shouldShowError(
                    matchedAddress?.[0],
                    matchscore
                )
                return {
                    matches: matchedAddress.length
                        ? [
                              {
                                  DeliveryAddress1: convertToLowercase(
                                      matchedAddress[0].DeliveryAddress1
                                  ),
                                  DeliveryAddress2: convertToLowercase(
                                      matchedAddress[0].DeliveryAddress2
                                  ),
                                  DeliveryAddress: convertToLowercase(
                                      matchedAddress[0].DeliveryAddress
                                  ),
                                  Locality: convertToLowercase(matchedAddress[0].Locality),
                                  AdministrativeArea: matchedAddress[0].AdministrativeArea,
                                  PostalCode: matchedAddress[0].PostalCode?.split('-')[0],
                                  AddressType: matchedAddress[0].ResCom_Flag === 'Res' ? 'R' : 'C'
                              }
                          ]
                        : [],
                    enteredAddress: enteredAddress
                        ? {
                              Address1: convertToLowercase(enteredAddress.Address1),
                              Address2: convertToLowercase(enteredAddress.Address2),
                              Locality: convertToLowercase(enteredAddress.Locality),
                              AdministrativeArea: enteredAddress.AdministrativeArea,
                              PostalCode: enteredAddress.PostalCode?.split('-')[0]
                          }
                        : {},
                    showError: showError && response.Number !== 2,
                    showAddressVerificationModal: showAddressVerificationModal
                }
            } catch (err) {
                console.error(err)
            }
        },
        async getAddressSuggestion(
            {text, addressId, addressType},
            setSuggestions,
            setValue,
            clearErrors,
            form
        ) {
            const apiDetails = await loqate.apiDetails()
            try {
                const endpoint = new URL(
                    addressType === 'Address'
                        ? apiDetails.loqateAddressRetrieve
                        : apiDetails.loqateAddressCapture
                )
                const method = 'GET'
                const body = null
                const params = {
                    Countries: apiDetails.country,
                    language: apiDetails.locale,
                    Key: apiDetails.loqateApiKey,
                    limit: apiDetails.addressLimit,
                    Text: text,
                    Container: (addressType !== 'Address' ? addressId : '') || '',
                    Id: (addressType !== 'Address' ? '' : addressId) || ''
                }
                Object.keys(params).forEach((key) => endpoint.searchParams.append(key, params[key]))
                const response = await loqate.loqateFetch(endpoint.href, method, body)
                if (addressType === 'Address') {
                    if (!response?.Items?.length) {
                        console.warn('No address found in Loqate response')
                        return
                    }
                    const selectedAddress = response.Items[0]
                    const {address1, address2} = splitLoqateAddressLines(selectedAddress)
                    window.loqateAddress1 = true
                    setValue('address1', address1)
                    setValue('address2', address2)
                    setValue('postalCode', selectedAddress.PostalCode || '')
                    setValue('stateCode', selectedAddress.ProvinceCode)
                    setValue('city', convertToLowercase(selectedAddress.City))
                    setValue('addressType', selectedAddress.Type === 'Commercial' ? 'C' : 'R')
                    setValue('addressFinder', '')
                    clearErrors(['address1', 'address2', 'postalCode', 'stateCode', 'city'])
                    if (form) {
                        form.trigger('address1')
                        form.trigger('address2')
                        form.trigger('city')
                    }
                    setSuggestions({})
                } else {
                    if (typeof setSuggestions === 'function') {
                        setSuggestions(
                            response?.Items &&
                                response?.Items?.length &&
                                (response?.Items[0]?.Error === LOQATE_UNKNOWN_KEY_ERROR ||
                                    response?.Items[0]?.Error === LOQATE_GENERIC_ERROR ||
                                    text?.length < 2)
                                ? {}
                                : response?.Items
                        )
                        if (document.getElementsByClassName('react-loqate-default-list').length) {
                            document
                                .getElementsByClassName('react-loqate-default-list')[0]
                                .scrollTo({top: 0})
                        }
                    } else if (response.Items && response.Items.length) {
                        return response?.Items ? {items: response?.Items} : {}
                    }
                }
            } catch (err) {
                console.error(err)
            }
        },
        async onAddressLookupType(text) {
            const apiDetails = await loqate.apiDetails()
            const endpoint = new URL(apiDetails.loqateAddressCapture)
            const method = 'GET'
            const params = {
                Countries: apiDetails.country,
                language: apiDetails.locale,
                Key: apiDetails.loqateApiKey,
                limit: apiDetails.loqateAddressLimit,
                Text: text,
                Container: '',
                Id: ''
            }
            Object.keys(params).forEach((key) => endpoint.searchParams.append(key, params[key]))
            const response = await loqate.loqateFetch(endpoint.href, method, null)
            return response.Items.filter((item) => item.Type === 'Address').map(
                ({Id: id, Text: name}) => ({id, name})
            )
        },
        async onAddressSelected(id) {
            const apiDetails = await loqate.apiDetails()
            const endpoint = new URL(apiDetails.loqateAddressRetrieve)
            const method = 'GET'
            const params = {
                Countries: apiDetails.country,
                language: apiDetails.locale,
                Key: apiDetails.loqateApiKey,
                limit: apiDetails.addressLimit,
                Container: id,
                Id: id
            }
            Object.keys(params).forEach((key) => endpoint.searchParams.append(key, params[key]))
            const response = await loqate.loqateFetch(endpoint.href, method, null)
            return response?.Items?.length
                ? {
                      id: id,
                      name: response.Items[0].Label,
                      street: response.Items[0].Street,
                      houseNumberOrName: response.Items[0].BuildingNumber,
                      postalCode: response.Items[0].PostalCode,
                      city: response.Items[0].City,
                      country: response.Items[0].CountryIso2,
                      stateOrProvince: response.Items[0].ProvinceCode,
                      addressType: response.Items[0].Type === 'Commercial' ? 'C' : 'R'
                  }
                : {}
        }
    }
}

export default useLoqate
